import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { useId } from 'react';
import { useForm } from 'react-hook-form';

import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FormattedMessage } from 'react-intl';
import { InvoiceData, InvoiceStatusType } from 'types/invoices';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';

interface Props {
    onClose: () => void;
    onReject: (reason: string) => Promise<void>;
    onDisagreeAndContinue?: (reason: string) => Promise<void>;
    invoice: InvoiceData;
    rejectToState: InvoiceStatusType;
    isRequestedToPostpone: boolean;
}

interface InvoiceRejectionFormData {
    reason: string;
}

const defaultValues: Partial<InvoiceRejectionFormData> = {
    reason: '',
};

const USED_CLASSIFIERS = ['INVOICE_STATUS_TYPE'];

const InvoiceWorkflowRejectModal = (props: Props) => {
    const { onClose, invoice, onReject, onDisagreeAndContinue, rejectToState, isRequestedToPostpone } = props;
    const { classifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const isJointInvoice = invoice.invoiceMatters && invoice.invoiceMatters.length > 1;
    const isNew = useIsNewDesign();
    // The new design explains each answer in a line its button points at, not in a tooltip.
    const continueLineId = useId();
    const returnLineId = useId();

    const { handleSubmit, control } = useForm<InvoiceRejectionFormData>({
        defaultValues: defaultValues,
    });

    const onSubmit = async (formData: InvoiceRejectionFormData) => {
        const comment = formData.reason;
        await onReject(comment);
        onClose();
    };

    const onSubmitDisagreeAndContinue = async (formData: InvoiceRejectionFormData) => {
        const comment = formData.reason;
        await onDisagreeAndContinue?.(comment);
        onClose();
    };

    let sendBackToPersonText;

    switch (invoice.invoiceStatusTypeCode) {
        case InvoiceStatusType.REVIEWED:
            sendBackToPersonText = <FormattedMessage id="invoices.form.invoice-sent-back-to-responsible-lawyer" />;
            break;
        case InvoiceStatusType.APPROVED:
            sendBackToPersonText = <FormattedMessage id="invoices.form.invoice-sent-back-to-supervising-partner" />;
            break;
        default:
            sendBackToPersonText = (
                <FormattedMessage
                    id="invoices.form.invoice-sent-back-to-status"
                    values={{ statusName: getClassifierText(classifiers?.['INVOICE_STATUS_TYPE'], rejectToState) }}
                />
            );
    }

    const request = invoice.invoiceTransitions?.[0];
    const disagreeDescription = isJointInvoice ? (
        <FormattedMessage id="invoices.form.reject-postpone-joint-number" values={{ number: invoice.invoiceNumber }} />
    ) : (
        <>
            <FormattedMessage
                id="invoices.form.postpone-requested"
                values={{ name: request?.changedByEmployee?.name || '-', number: invoice.invoiceNumber }}
            />
            {request?.comment ? (
                <>
                    {' '}
                    <FormattedMessage id="invoices.alerts.reason" values={{ reason: request.comment }} />
                </>
            ) : null}
        </>
    );

    const buttons = isRequestedToPostpone
        ? [
              <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                  <FormattedMessage id="cancel" />
              </Button>,
              <Button
                  {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                  key="disagree-continue"
                  aria-describedby={isNew ? continueLineId : undefined}
                  onClick={() => {
                      void handleSubmit(onSubmitDisagreeAndContinue)();
                  }}
              >
                  <FormattedMessage id="invoices.form.disagree-continue" />
              </Button>,
              <Button
                  {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                  key="disagree-return"
                  aria-describedby={isNew ? returnLineId : undefined}
                  onClick={() => {
                      void handleSubmit(onSubmit)();
                  }}
              >
                  <FormattedMessage id="invoices.form.disagree-return" />
              </Button>,
          ]
        : [
              <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                  <FormattedMessage id="cancel" />
              </Button>,
              <Button
                  {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                  key="send-to-rp"
                  onClick={() => {
                      void handleSubmit(onSubmit)();
                  }}
              >
                  <FormattedMessage id={isNew ? 'invoices.actions.reject' : 'save'} />
              </Button>,
          ];

    return (
        <>
            <ModalHeader
                kit
                title={
                    isRequestedToPostpone
                        ? t('invoices.form.disagree-to-postpone')
                        : t('invoices.form.reject-invoice', { number: invoice.invoiceNumber })
                }
                description={isRequestedToPostpone ? disagreeDescription : sendBackToPersonText}
                onCancel={onClose}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                <div className="grid gap-4">
                    {/* The new design says this in the header's description. */}
                    {!isNew && (
                        <p className="text-body1">
                            {isRequestedToPostpone ? (
                                <FormattedMessage
                                    id={
                                        isJointInvoice
                                            ? 'invoices.form.reject-postpone-joint-number'
                                            : 'invoices.form.reject-postpone-number'
                                    }
                                    values={{ number: invoice.invoiceNumber }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="invoices.form.reject-invoice-number"
                                    values={{ number: invoice.invoiceNumber, reason: sendBackToPersonText }}
                                />
                            )}
                        </p>
                    )}
                    <TextAreaInput
                        name="reason"
                        control={control}
                        label="invoices.form.write-specific-reason"
                        required
                        minRows={isNew ? 4 : undefined}
                    />
                    {isNew && isRequestedToPostpone && (
                        <div className="text-muted-foreground flex flex-col gap-1 text-sm">
                            <p id={continueLineId}>
                                <span className="text-foreground font-medium">
                                    <FormattedMessage id="invoices.form.disagree-continue" />
                                </span>
                                : <FormattedMessage id="invoices.form.disagree-continue-tooltip" />
                            </p>
                            <p id={returnLineId}>
                                <span className="text-foreground font-medium">
                                    <FormattedMessage id="invoices.form.disagree-return" />
                                </span>
                                : <FormattedMessage id="invoices.form.disagree-return-tooltip" />
                            </p>
                        </div>
                    )}
                </div>
            </div>
            <ModalFooterSimple
                kit
                buttons={buttons}
                tooltips={
                    isNew
                        ? undefined
                        : {
                              'disagree-return': t('invoices.form.disagree-return-tooltip'),
                              'disagree-continue': t('invoices.form.disagree-continue-tooltip'),
                          }
                }
            />
        </>
    );
};

export default InvoiceWorkflowRejectModal;
